import { useEffect, useState } from "react";
import { Crown, Medal, Award, Loader2, MessageSquare, Gift, UserCheck, Trophy } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function rankBadge(rank) {
  if (rank === 1) return { icon: Crown, color: "text-yellow-400", bg: "bg-yellow-400/15 border-yellow-400/40" };
  if (rank === 2) return { icon: Medal, color: "text-slate-700", bg: "bg-slate-300/15 border-slate-300/40" };
  if (rank === 3) return { icon: Award, color: "text-amber-600", bg: "bg-amber-600/15 border-amber-600/40" };
  return null;
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/leaderboard");
        setData(r.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Rangliste…</div>
    );

  return (
    <div data-testid="leaderboard-page" className="space-y-5">
      <div>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#4DBFFF] mb-1">Community</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter flex items-center gap-2">
          Rangliste <Trophy className="w-7 h-7 text-yellow-400" />
        </h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-1">
          Punkte: 1 pro Nachricht · 5 pro Verlosungs-Teilnahme · 10 pro Freundschaft.
        </p>
      </div>

      {/* My rank */}
      {data?.me && (
        <div className="bg-gradient-to-r from-[#1D5BFF]/20 to-[#4DBFFF]/10 border border-[#1D5BFF]/40 rounded-2xl p-4 flex items-center gap-3" data-testid="my-rank">
          <div className="font-display text-3xl font-black text-slate-900 w-12 text-center">#{data.me.rank}</div>
          <img src={fileUrl(data.me.avatar_url)} alt={data.me.name} className="w-12 h-12 rounded-full object-cover border border-[#1D5BFF]/50" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900 truncate">{data.me.name} <span className="text-slate-600 text-xs">(Du)</span></div>
            <div className="text-xs text-slate-600">{data.me.points} Punkte · {data.me.messages} Nachrichten</div>
          </div>
        </div>
      )}

      {/* Top list */}
      <div className="bg-[#FFFFFF] border border-slate-200 rounded-2xl divide-y divide-white/5" data-testid="leaderboard-list">
        {data?.top?.length === 0 && (
          <div className="p-8 text-center text-slate-500">Noch keine Daten – sei der/die Erste!</div>
        )}
        {data?.top?.map((row) => {
          const badge = rankBadge(row.rank);
          const isMe = user && row.user_id === user.id;
          return (
            <div
              key={row.user_id}
              data-testid={`rank-row-${row.rank}`}
              className={`flex items-center gap-3 p-3 sm:p-4 ${isMe ? "bg-[#1D5BFF]/5" : ""}`}
            >
              <div className={`font-display text-lg sm:text-xl font-black w-9 text-center ${row.rank <= 3 ? "text-slate-900" : "text-slate-500"}`}>
                {row.rank}
              </div>
              <div className="relative shrink-0">
                <img src={fileUrl(row.avatar_url)} alt={row.name} className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover border border-slate-200" />
                {badge && (
                  <div className={`absolute -bottom-1 -right-1 p-1 rounded-full border ${badge.bg}`}>
                    <badge.icon className={`w-3.5 h-3.5 ${badge.color}`} />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                  {row.name}
                  {row.role === "admin" && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1D5BFF]/20 text-[#4DBFFF] border border-[#1D5BFF]/40">Admin</span>
                  )}
                </div>
                <div className="text-[11px] sm:text-xs text-slate-500 truncate flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {row.messages}</span>
                  <span className="flex items-center gap-1"><Gift className="w-3 h-3" /> {row.giveaway_entries}</span>
                  <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" /> {row.friends}</span>
                  {row.location && <span className="hidden sm:inline">· {row.location}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-black text-base sm:text-lg text-slate-900">{row.points}</div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Punkte</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
